/**
 * Module 6D Phase 7 — MFA challenge + enrollment screens.
 *
 * Two states managed by `AdminAuthProvider.mfaChallenge`:
 *   1. requires_enroll=true → first-time enrollment flow:
 *        init → show QR → confirm with TOTP code → show backup codes
 *      Then user is automatically transitioned to a fresh sign-in.
 *   2. requires_enroll=false → TOTP challenge flow:
 *        enter 6-digit code (or a backup code) → exchange for full session
 *
 * The pending token lives only in component state via the provider.
 * It is NEVER persisted to localStorage / sessionStorage.
 */
import React, { useEffect, useState } from "react";
import { ShieldCheck, AlertOctagon, KeyRound, Loader2 } from "lucide-react";
import { adminApi, useAdminAuth } from "@/lib/admin_auth";


export default function AdminMfaScreen() {
  const { mfaChallenge, completeMfa, setMfaChallenge } = useAdminAuth();
  if (!mfaChallenge) return null;
  return mfaChallenge.requires_enroll
    ? <Enrollment pending={mfaChallenge} onComplete={() => setMfaChallenge(null)} />
    : <Challenge pending={mfaChallenge} onSuccess={completeMfa} />;
}


function Challenge({ pending, onSuccess }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e?.preventDefault();
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/auth/mfa/verify", {
        pending_token: pending.pending_token,
        code: code.trim(),
      });
      // Server returns the full admin token + admin shape.
      onSuccess(data.admin, data.token);
    } catch (err) {
      setError("Invalid code. Try again or use a backup code.");
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Frame title="Verify your identity"
            subtitle="Enter the 6-digit code from your authenticator app, or a one-shot backup code.">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
            Authentication code
          </div>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoFocus
            inputMode="text"
            autoComplete="one-time-code"
            placeholder="123456 or BACKUP-CODE"
            data-testid="admin-mfa-code-input"
            className="w-full bg-zinc-900 border border-zinc-800 text-zinc-100 px-3 py-3 text-base tracking-widest font-mono"
            style={{ borderRadius: 0 }}
          />
        </div>
        {error ? (
          <div className="text-xs text-red-400 flex items-center gap-1.5"
                data-testid="admin-mfa-error">
            <AlertOctagon size={12} /> {error}
          </div>
        ) : null}
        <button
          type="submit"
          disabled={busy || code.trim().length < 6}
          data-testid="admin-mfa-submit"
          className="w-full bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold py-3 disabled:opacity-50"
          style={{ borderRadius: 0 }}
        >
          {busy ? <Loader2 className="animate-spin inline" size={14} /> : "Continue"}
        </button>
        <div className="text-[11px] text-zinc-500 leading-relaxed pt-3">
          Lost your authenticator? Use one of your backup codes here.
          If you&apos;ve exhausted those too, contact another super-admin
          for peer reset.
        </div>
      </form>
    </Frame>
  );
}


function Enrollment({ pending, onComplete }) {
  const [step, setStep] = useState("loading");   // loading | qr | confirm | done
  const [secret, setSecret] = useState(null);
  const [otpUrl, setOtpUrl] = useState(null);
  const [email, setEmail] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [backupCodes, setBackupCodes] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await adminApi.post("/admin/auth/mfa/enroll/init", {
          pending_token: pending.pending_token,
        });
        if (cancelled) return;
        setSecret(data.secret);
        setOtpUrl(data.otpauth_url);
        setEmail(data.email);
        setStep("qr");
      } catch (err) {
        if (cancelled) return;
        setError("Enrollment failed to initialize. Reload to retry.");
        setStep("error");
      }
    })();
    return () => { cancelled = true; };
  }, [pending.pending_token]);

  const confirm = async (e) => {
    e?.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await adminApi.post("/admin/auth/mfa/enroll/confirm", {
        pending_token: pending.pending_token,
        code: code.trim(),
      });
      setBackupCodes(data.backup_codes);
      setStep("done");
    } catch (err) {
      setError("Invalid code. Make sure your authenticator clock is in sync.");
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  if (step === "loading") {
    return <Frame title="Enrolling MFA…" subtitle="Generating your TOTP secret."><Loader2 className="animate-spin text-zinc-500" /></Frame>;
  }
  if (step === "error") {
    return <Frame title="Enrollment failed" subtitle={error}><a href="/admin/login" className="text-amber-400 text-sm">Back to sign in</a></Frame>;
  }
  if (step === "qr") {
    // Render a Google-Charts QR using the otpauth URL — no client lib dep.
    const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(otpUrl)}`;
    return (
      <Frame title="Scan with your authenticator"
              subtitle={`Account: ${email}`}>
        <div className="flex flex-col items-center gap-4" data-testid="admin-mfa-enroll-qr">
          <img src={qrSrc} alt="MFA QR" width={220} height={220}
                style={{ background: "#fff", padding: 8 }} />
          <details className="text-xs text-zinc-500 self-stretch">
            <summary className="cursor-pointer">Can&apos;t scan? Enter secret manually.</summary>
            <div className="font-mono text-zinc-300 mt-2 break-all p-2 border border-zinc-800"
                  data-testid="admin-mfa-enroll-secret">{secret}</div>
          </details>
          <button
            onClick={() => setStep("confirm")}
            data-testid="admin-mfa-enroll-next"
            className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold py-2 px-6"
            style={{ borderRadius: 0 }}
          >I&apos;ve added it — continue</button>
        </div>
      </Frame>
    );
  }
  if (step === "confirm") {
    return (
      <Frame title="Confirm enrollment"
              subtitle="Enter the 6-digit code your authenticator now shows.">
        <form onSubmit={confirm} className="space-y-4">
          <input
            type="text" value={code}
            onChange={(e) => setCode(e.target.value)}
            inputMode="numeric" autoFocus maxLength={6}
            placeholder="123456"
            data-testid="admin-mfa-enroll-confirm-input"
            className="w-full bg-zinc-900 border border-zinc-800 text-zinc-100 px-3 py-3 text-2xl tracking-widest font-mono text-center"
            style={{ borderRadius: 0 }}
          />
          {error ? <div className="text-xs text-red-400">{error}</div> : null}
          <button type="submit" disabled={busy || code.length < 6}
            data-testid="admin-mfa-enroll-confirm-submit"
            className="w-full bg-amber-500 text-zinc-950 font-semibold py-3 disabled:opacity-50"
            style={{ borderRadius: 0 }}>
            {busy ? "…" : "Confirm enrollment"}
          </button>
        </form>
      </Frame>
    );
  }
  if (step === "done") {
    return (
      <Frame title="MFA enabled"
              subtitle="Save these backup codes NOW. Each is one-shot. They're shown only this once."
              data-testid="admin-mfa-enroll-done">
        <div className="mb-4 grid grid-cols-2 gap-2 font-mono text-sm"
              data-testid="admin-mfa-backup-codes">
          {(backupCodes || []).map((c, i) => (
            <div key={i} className="bg-zinc-900 border border-zinc-800 py-2 text-center text-amber-200"
                  data-testid={`admin-mfa-backup-code-${i}`}>{c}</div>
          ))}
        </div>
        <div className="text-[11px] text-zinc-500 leading-relaxed mb-4">
          Store these in your password manager. If you lose your
          authenticator AND these codes, you&apos;ll need a peer super-admin
          to reset your MFA, or the break-glass procedure.
        </div>
        <button onClick={() => { onComplete(); window.location.replace("/admin/login"); }}
          data-testid="admin-mfa-enroll-finish-btn"
          className="w-full bg-amber-500 text-zinc-950 font-semibold py-3"
          style={{ borderRadius: 0 }}>
          I&apos;ve saved them — sign in
        </button>
      </Frame>
    );
  }
  return null;
}


function Frame({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center px-6"
          data-testid="admin-mfa-frame">
      <div className="max-w-md w-full">
        <div className="flex items-center gap-3 mb-6">
          <ShieldCheck className="w-7 h-7 text-amber-400" />
          <div>
            <div className="text-[10px] tracking-[0.32em] uppercase text-amber-400/70">
              Lead Studio — Internal
            </div>
            <h1 className="text-xl tracking-tight" style={{ fontFamily: "serif" }}>{title}</h1>
          </div>
        </div>
        {subtitle ? <p className="text-sm text-zinc-400 leading-relaxed mb-6">{subtitle}</p> : null}
        {children}
      </div>
    </div>
  );
}
